import { StripHTML } from '@/utils/utils';
import type { ETemplateRevision } from 'rtk/TemplateSlice';
import { useAppSelector } from 'rtk/query/hooks';
import type { TSelectBase } from 'types/general.types';
import { EQuestionType } from '@hubertai/screening-question-types';
import type { TLanguageSettings, TVisibility } from 'types/template/template.types';

export function useTemplateLanguageSettings(templateId: string | null): TLanguageSettings {
  const languageSettings = useAppSelector((state) => state.Template?.present?.c?.language_settings);

  if (!templateId) {
    return {
      // @ts-ignore
      starting_language: '',
      list: [],
      allowed_languages: [],
    };
  }

  if (languageSettings) {
    return languageSettings;
  }

  return {
    // @ts-ignore
    starting_language: '',
    list: [],
    allowed_languages: [],
  };
}

export function useTemplateVisibility(): TVisibility | undefined {
  return useAppSelector<TVisibility | undefined>((state) => state.Template?.present?.visibility);
}
export function useTemplateId(): string {
  return useAppSelector<string>((state) => state.Template?.present?.cTemplateId);
}

export function useTemplateClientId(): string | undefined {
  return useAppSelector((state) => state.Template?.present?.c?.client_id);
}

export function useTemplateAccessGroupIds(): string[] | undefined {
  return useAppSelector((state) => state.Template?.present?.c?.accessGroupIds);
}

export function useTemplateIsReadOnly(): boolean {
  return useAppSelector((state) => state.Template?.present?.isReadOnly);
}

export function useTemplateRevision(): ETemplateRevision {
  return useAppSelector((state) => state.Template?.present?.revision);
}

export function useTemplateReferenceId(): string {
  return useAppSelector((state) => state.Template?.present?.templateReferenceId);
}

export function useTemplateProcessId(): string {
  return useAppSelector((state) => state.Template?.present?.processId);
}

export function useTemplateName(): string {
  return useAppSelector((state) => state.Template?.present?.c?.name ?? '');
}

export function useTemplateQuestions(): any {
  const library = useAppSelector((state) => state.Library);
  const templateId = useTemplateId();
  const templateLanguages = useTemplateLanguageSettings(templateId);
  const startingLanguage = templateLanguages.starting_language;
  const questionIds = useAppSelector((state) => state.Template?.present?.c?.outline.question_list);

  const list: TSelectBase[] = [];

  questionIds?.map((questionId) => {
    const question = library[questionId];
    if (!question) {
      list.push({
        label: `Question missing in library ${questionId}`,
        value: questionId,
      });
      return;
    }

    if ('text' in question && startingLanguage in question.text) {
      if (![EQuestionType.SingleSelect, EQuestionType.MultipleSelect].includes(question.type)) {
        return;
      }

      let text = question.text[startingLanguage]?.join(' ') ?? '';
      text = StripHTML(text) ?? '';

      list.push({
        label: text ?? `Erroneous question text ${questionId}`,
        value: questionId,
      });
    } else {
      list.push({
        label: `Erroneous question ${questionId}`,
        value: questionId,
      });
    }
  });

  return list;
}
